import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { format, isValid } from 'date-fns';
import { get as _get, has as _has } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import {
  BillAccountActions,
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { paymentHistoryQuery } from '@amfam/billing/payment-history/data-access';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util';
import { env } from '@amfam/environment';
import { Policy } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
// Store
import {
  BillAccount,
  BillingAnalytics,
  BillingPaymentPaths,
  ONLINE_BILLING,
  Payment,
  PolicyChangeRequestType,
  SilentRegistrationPayload
} from '@amfam/shared/models';
import { CurrencyUSDPipe, DocumentPipeService, PrettyBillingAcctNum } from '@amfam/shared/ui/pipes';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  openWindowBlob,
  UtilService,
  WindowRef
} from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as fromDetailCommonSelector from '../../+state/selectors';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'bill-account-detail-card',
  templateUrl: './bill-account-detail-card.component.html',
  styleUrls: ['./bill-account-detail-card.component.scss'],
  standalone: false
})
export class BillAccountDetailCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() billAccount: BillAccount;
  @Output() silentRegistrationFailureEvent = new EventEmitter();

  classicPastDueEnabled = false;
  tpiEnabled = false;
  tpiRequestChangeEnabled = false;
  loading = true;
  contactNumber: string;
  onlineBillingText = ONLINE_BILLING;
  // Nickname or Bill Account Number
  billAccountName = '';

  // Autopay Enrollment indicator
  matchingAutopayRule = null;
  matchingAutopayRuleDate: string;
  matchingAutopayRuleAmount: string;

  // Pending payment details
  hasPendingPayment = false;
  pendingPaymentDescription = null;
  pendingPaymentDate = null;
  showAutopayButton = false;
  showPayBillButton = false;
  isEligibleAutoPay = false;
  isClassicPastDue = false;

  makePaymentBtnContent: string;
  setupAutoPayBtnContent: string;
  documentApi: string;
  billAccountDisclaimer: string;
  lastSuccessfulPayment$: Observable<Payment>;
  public thirdPartyInterest = [];
  public editLenderBtnName: string;
  public matchedBillAccountNotificationState: BillingNotificationsState;
  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;
  public isAdminApp = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);

  private automaticPayments = [];
  private scheduledPayments = [];
  private eligibleAccounts = [];
  private policiesWithTPI: Policy[] = [];

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private featureFlagService: FeatureFlagService,
    public roleService: ImpersonateRolesService,
    public spinner: LoadingSpinnerService,
    private store: Store,
    private transmuteService: BillAccountTransmuteService,
    private utilService: UtilService,
    private copyService: CopyService,
    private applicationService: ApplicationService,
    private autoPayUtil: AutoPayUtilService,
    private win: WindowRef,
    private documentPipeService: DocumentPipeService,
    private router: Router,
    private analyticsFacade: AnalyticsFacade,
    private sanitizer: DomSanitizer,
    private deviceService: DeviceDetectorService
  ) {}

  ngOnInit() {
    this.classicPastDueEnabled = this.featureFlagService.isEnabled('classic_past_due');
    this.tpiEnabled = this.featureFlagService.isEnabled('tpi_paid_by');
    this.tpiRequestChangeEnabled = this.featureFlagService.isEnabled('tpi_request_change');
    this.editLenderBtnName = this.copyService.getCopy(
      'policy.thirdPartyInterest.editLender.buttonName'
    );

    this.billAccountDisclaimer = this.copyService.getCopy(
      this.billAccount?.policyList?.length > 1
        ? 'policy.thirdPartyInterest.billing.disclaimerForCombinedPolicy'
        : 'policy.thirdPartyInterest.billing.disclaimer',
      {
        editLender: !!this.tpiRequestChangeEnabled
      }
    );
    this.makePaymentBtnContent = this.copyService.getCopy('billing.makePayment');
    this.setupAutoPayBtnContent = this.copyService.getCopy('billing.setUpAutoPay');
    this.lastSuccessfulPayment$ = this.store
      .select(paymentHistoryQuery.selectPayments)
      .pipe(
        map((payments: Payment[]) =>
          payments.find(
            (payment: Payment) =>
              _get(payment, 'billAccounts[0].billAccountNumber') ===
                this.billAccount.billAccountNumber &&
              _get(payment, 'paymentStatus') === 'SUCCESSFUL'
          )
        )
      );
    const billAccountNumber = _get(this.billAccount, 'billAccountNumber');

    this.store
      .select(fromDetailCommonSelector.selectCombinedAutoPayState(billAccountNumber))
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        // Determine if we're still loading data
        this.loading = state.loading;
        this.eligibleAccounts = state.eligibleBillAccts;

        // Get the list automatic payments
        this.automaticPayments = state.automaticPayments;

        this.matchedBillAccountNotificationState =
          state.matchedBillAccountNotification as BillingNotificationsState;

        // determine if we should show the enrolled in autopay indicator
        this.determineAutopayStatus();

        // Get the list of scheduled payments
        this.scheduledPayments = state.scheduledPayments;

        // Determine if we should show a pending payment
        this.getPendingPayment();

        this.isEligibleForAutoPay();

        // Determine if we should show the autopay button
        this.showAutopayButton = this.showAutopayButtonInit();

        // Determine if we should show the pay bill button
        this.showPayBillButton = this.showPayBillButtonInit();

        this.policiesWithTPI = state.policiesWithTPI;

        this.determineTPI();
      });

    // Set the contact number.
    this.contactNumber = env.numberLink.split(':')[1];
    this.documentApi = env.documentApi;
  }

  ngOnChanges() {
    this.deriveAccountName();
    this.determineAutopayStatus();
    this.getPendingPayment();
    this.isEligibleForAutoPay();
    // Determine if we should show the payment widget
    this.showPayBillButton = this.showPayBillButtonInit();
    // Determine if we should show the autopay button
    this.showAutopayButton = this.showAutopayButtonInit();

    // To show classic past due, we must also have the billing statement and minimum due breakdown
    // return, to have the relevant information to show on the card
    this.isClassicPastDue = this.showClassicPastDueBilling();

    this.determineTPI();
  }
  onEditLender() {
    this.analyticsFacade.trackButtonClick(BillingAnalytics.buttonBillingPaymentsEditLenderInfo);
    this.router.navigate(['/policies/changerequest'], {
      state: {
        changeRequestType: PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE,
        selectedPolicyNumber: this.policiesWithTPI[0]?.policyNumber || ''
      }
    });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  public registerOrAutopay() {
    // AUTOPAY ENTRY POINT
    this.autoPayUtil.routeToMultipleAutoPaySelection(
      `/billing/${this.billAccount.billAccountNumber}`,
      [this.billAccount.billAccountNumber]
    );
  }

  public registerOrPay() {
    this.store.dispatch(
      pendingPaymentActions.AddPendingPaymentAction({
        billAccountNumber: this.billAccount.billAccountNumber
      })
    );

    if (_get(this.billAccount, 'billingMethod') !== ONLINE_BILLING) {
      let routeToMakeAPayment: string;
      // ONE TIME PAYMENT ENTRYPOINT
      if (this.featureFlagService.isEnabled('simplified_payment')) {
        routeToMakeAPayment = BillingPaymentPaths.ADD_ONE_TIME_PAYMENT;
      } else {
        this.store
          .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
          .pipe(take(1))
          .subscribe(billAccounts => {
            if (billAccounts.length === 1) {
              routeToMakeAPayment = BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT;
            } else {
              routeToMakeAPayment = BillingPaymentPaths.ADD_ONE_TIME_PAYMENT;
            }
          });
      }
      // If the account isn't registered and pcm is enabled do silent registration.

      this.registerAndRoute([routeToMakeAPayment]);
    } else {
      // If the account is registered navigate to payment flow.
      // ONE TIME PAYMENT ENTRYPOINT
      if (this.featureFlagService.isEnabled('simplified_payment')) {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
          })
        );
      } else {
        this.store
          .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
          .pipe(take(1))
          .subscribe(billAccounts => {
            if (billAccounts.length === 1) {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                })
              );
            } else {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                })
              );
            }
          });
      }
    }
  }
  public openStatementOnAndroid(link: string) {
    let windowRef;
    if (!window.navigator) {
      windowRef = this.win.nativeWindow.open();
    }
    this.documentPipeService
      .retrieveDocument(link)
      .pipe(take(1))
      .subscribe(
        res => {
          if (!res) {
            if (windowRef) {
              windowRef.close();
            }
          }
          const url = this.sanitizer.sanitize(SecurityContext.URL, res);
          this.spinner.stop();
          openWindowBlob(this.win, url, res, windowRef, 'document.pdf');
        },
        () => {
          if (windowRef) {
            windowRef.close();
          }
        }
      );
  }

  public openStatement(link: string) {
    this.analyticsFacade.trackButtonClick(BillingAnalytics.buttonViewCurrentBill);
    this.spinner.start();
    if (this.deviceService.device.toLowerCase() === 'android') this.openStatementOnAndroid(link);
    else {
      let windowRef;
      // eslint-disable-next-line prefer-const
      windowRef = this.win.nativeWindow.open();
      this.documentPipeService
        .retrieveDocument(link)
        .pipe(take(1))
        .subscribe(
          res => {
            if (!res) {
              if (windowRef) {
                windowRef.close();
              }
            }
            const url = this.sanitizer.sanitize(SecurityContext.URL, res);
            this.spinner.stop();
            windowRef.location.href = url;
          },
          () => {
            if (windowRef) {
              windowRef.close();
            }
          }
        );
    }
  }

  private determineTPI() {
    this.thirdPartyInterest =
      this.policiesWithTPI
        ?.find(
          (policy: Policy) => policy?.billingAccountNumber === this.billAccount?.billAccountNumber
        )
        ?.policyRoles?.filter(el => el.roleType === 'BillTo')
        ?.slice(0, 3) || [];
  }

  private deriveAccountName() {
    this.billAccountName = _get(
      this.billAccount,
      'billingPreferences.accountNickName',
      'Account: ' + new PrettyBillingAcctNum().transform(this.billAccount.billAccountNumber)
    );
  }

  private determineAutopayStatus() {
    // TODO move all this logic to a shared place to share with overview, etc
    this.matchingAutopayRule = this.automaticPayments.find(rule => {
      if (rule) {
        return rule.billAccountNumber === this.billAccount.billAccountNumber && !rule.hasError;
      }
      return false;
    });
    if (
      isValid(new Date(_get(this.matchingAutopayRule, 'nextPaymentDate', ''))) &&
      _get(this.matchingAutopayRule, 'predictedDollarAmount', 'UNKNOWN') !== 'UNKNOWN'
    ) {
      this.matchingAutopayRuleDate = format(
        _get(this.matchingAutopayRule, 'nextPaymentDate', ''),
        'MM/DD/YYYY'
      );
      this.matchingAutopayRuleAmount = new CurrencyUSDPipe().transform(
        _get(this.matchingAutopayRule, 'predictedDollarAmount', '')
      );
    }
  }

  private isEligibleForAutoPay() {
    return (this.isEligibleAutoPay = this.eligibleAccounts.some(eligibleAccount =>
      eligibleAccount.billAccountNumber === this.billAccount.billAccountNumber ? true : false
    ));
  }

  private getPendingPayment() {
    /*
      Show a pending payment in the header:
      1. The account is enrolled in AFT (OR)
      2. The account has a scheduled payment (created via autopay or manually)

      The pending payment, if shown will be either:
      1. The paymentDueDate if enrolled in AFT (OR)
      2. The earliest scheduled payment date (regardless of created by autopay or manually)
    */

    // Initialize the fields
    this.hasPendingPayment = false;
    this.pendingPaymentDescription = null;
    this.pendingPaymentDate = null;

    // Handle AFT-enrolled bill accounts.
    if (_get(this, 'billAccount.enabledAFT')) {
      return;
    }

    // Check for matching scheduled payments (limited to bill account payment portions)
    const matchingPayments = [];

    this.scheduledPayments.forEach(payment => {
      if (_has(payment, 'billAccounts')) {
        const matchingBillAccountPayment = payment.billAccounts.find(
          billAccountPayment =>
            billAccountPayment.billAccountNumber === this.billAccount.billAccountNumber
        );

        if (matchingBillAccountPayment) {
          matchingPayments.push({
            autoPayIndicator: payment.autoPayIndicator,
            paymentAmount: matchingBillAccountPayment.billAcctPaymentAmount,
            paymentDate: payment.receivedDate
          });
        }
      }
    });

    // Sort any matching payments
    matchingPayments.sort((a, b) =>
      String(a['paymentDate']).localeCompare(String(b['paymentDate']))
    );

    if (matchingPayments.length > 0) {
      this.pendingPaymentDescription =
        matchingPayments[0].autoPayIndicator === 'true' ? 'Automatic Payment' : 'Scheduled Payment';
      this.pendingPaymentDate = matchingPayments[0].paymentDate;
      this.hasPendingPayment = true;
    }
  }

  // Takes one parameter, the path to route to after silent registration
  private registerAndRoute(path: string[]): void {
    this.spinner.start({
      blockActions: true
    });
    const correlationId = this.utilService.generateId();
    const silentRegistrationPayload: SilentRegistrationPayload[] =
      this.transmuteService.buildSilentRegistrationPayload(
        [this.billAccount.billAccountNumber],
        correlationId
      );
    this.store.dispatch(
      BillAccountActions.billAccountSilentRegistration(silentRegistrationPayload)
    );
    this.store
      .select(BillAccountsSelectors.selectBillAccounts)
      .pipe(
        mergeMap(billAccounts => billAccounts),
        withLatestFrom(
          this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities),
          this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
        ),
        filter(
          ([billingAccount, matchedBillAccountNotificationState]) =>
            matchedBillAccountNotificationState[billingAccount.billAccountNumber].correlationId ===
              correlationId &&
            (!!billingAccount.registrationDate ||
              !!matchedBillAccountNotificationState[billingAccount.billAccountNumber]
                .updateRegistrationError)
        ),
        take(1)
      )
      .subscribe(([billAccount]) => {
        this.spinner.stop();
        // Route to make a payment section if registration was successful
        if (_get(billAccount, 'registrationDate')) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [path[0]]
            })
          );
        }
        this.silentRegistrationFailureEvent.emit();
      });
  }

  private showClassicPastDueBilling(): boolean {
    return (
      this.classicPastDueEnabled &&
      _get(this.billAccount, 'pastDue') &&
      !_get(this.billAccount, 'isAdvance') &&
      _get(this.billAccount, 'billingStatement') &&
      !!_get(this.billAccount, 'minimumAmountDueBreakdown')
    );
  }

  private showAutopayButtonInit(): boolean {
    /*
      Show autopay button if:
      1. The account isn't setup for AFT AND
      2. The account isn't enrolled elsewhere AND
      3. The account isn't readonly once pcm is enabled.
      4. The account isn't already enrolled in autopay
    */

    return (
      !_get(this, 'billAccount.readOnly') && !this.matchingAutopayRule && this.isEligibleAutoPay
    );
  }

  private showPayBillButtonInit(): boolean {
    /*
      Show payment button if:
      1. The account isn't setup for AFT AND
      2. The account isn't enrolled elsewhere AND
      3. The account isn't already enrolled in autopay AND
      4. The account balance is > 0 - from a discussion with the PO & JJC
      5. The account isn't readonly once pcm is enabled.
      6. TODO: The account doesn't have a scheduled payment >= minimumAmountDue
    */

    return (
      !_get(this, 'billAccount.readOnly') &&
      !this.matchingAutopayRule &&
      _get(this, 'billAccount.accountBalance') > 0
    );
  }
}
